import React, { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { Product } from '@domain/product/models/Product';
import { ProductMode } from '@domain/product/models/ProductMode';
import LeadTimeDisplay from '../catalog/LeadTimeDisplay';
import LeadTimeDropdown from '../catalog/LeadTimeDropdown';
import { useProductUpdate } from '@features/products/hooks/useProductUpdate';

const MODE_LABELS: Record<ProductMode, string> = {
  [ProductMode.REVENUE_OPTIMIZED]: 'Revenue optimized',
  [ProductMode.COST_OPTIMIZED]: 'Cost optimized',
  [ProductMode.BALANCED]: 'Balanced',
};

function formatWeeksFromDays(days: number | undefined): string {
  if (days == null) return '—';
  const weeks = days / 7;
  if (weeks === 0) return '0 weeks';
  return `${Number.isInteger(weeks) ? weeks : weeks.toFixed(1)} weeks`;
}

interface ProductOrderingSettingsProps {
  product: Product;
}

const ProductOrderingSettings: React.FC<ProductOrderingSettingsProps> = ({ product }) => {

  const modeLabel =
    product.mode != null && product.mode in MODE_LABELS
      ? MODE_LABELS[product.mode as ProductMode]
      : product.mode ?? '—';

  const [leadTimeAnchorEl, setLeadTimeAnchorEl] = useState<HTMLElement | null>(null);
  const [leadTimeOpen, setLeadTimeOpen] = useState(false);

  const { updateProduct } = useProductUpdate();

  const handleLeadTimeClick = (event: React.MouseEvent<HTMLElement>) => {
    setLeadTimeAnchorEl(event.currentTarget);
    setLeadTimeOpen(true);
  };

  const handleLeadTimeClose = () => {
    setLeadTimeAnchorEl(null);
    setLeadTimeOpen(false);
  };

  const handleLeadTimeConfirm = (values: {
    productionDuration?: number;
    processingDuration?: number;
    defaultShipmentMethodId?: string;
  }) => {
    updateProduct({
      productId: product.id,
      updateData: {
        productionDuration: values.productionDuration,
        processingDuration: values.processingDuration,
        ...(values.defaultShipmentMethodId != null
          ? { defaultShipmentMethodId: values.defaultShipmentMethodId }
          : {}),
      },
    });
    handleLeadTimeClose();
  };

  return (
    <>
      <BorderedBlock header="Ordering settings">
        <Grid container spacing={3} direction="row" alignItems="flex-start">
          <Grid item xs={3}>
            <Typography variant="subtitle2" color="text.secondary">
              Lead time
            </Typography>
            <LeadTimeDisplay product={product} onClick={handleLeadTimeClick} />
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="text.secondary">
              Order amount
            </Typography>
            <Typography variant="body1">{formatWeeksFromDays(product.minimumOrderAmountDays)}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="text.secondary">
              Weeks of buffer
            </Typography>
            <Typography variant="body1">{formatWeeksFromDays(product.minimumDaysOfStockInReserve)}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="subtitle2" color="text.secondary">
              Mode
            </Typography>
            <Typography variant="body1">{modeLabel}</Typography>
          </Grid>
        </Grid>
      </BorderedBlock>

      <LeadTimeDropdown
        product={product}
        anchorEl={leadTimeAnchorEl}
        open={leadTimeOpen}
        onClose={handleLeadTimeClose}
        onConfirm={handleLeadTimeConfirm}
        isLoading={false}
      />

    </>
  );
};

export default ProductOrderingSettings;
